<script setup lang="ts">
  import { ref, watch } from 'vue'
  import {
    Button,
    Dialog,
    Icon,
    Input,
    FormControl,
    FormField,
    FormItem,
    toast,
  } from '@rapoo/element'
  import { useForm } from 'vee-validate'
  import { toTypedSchema } from '@vee-validate/zod'
  import { z } from 'zod'
  import { useLocaleStore, useUserStore } from '@/stores'
  import { userBind } from '@/service/api/user'

  const isOpen = defineModel<boolean>('open')

  const showPassword = ref(false)
  const { updateUserInfo } = useUserStore()
  const { t } = useLocaleStore()

  const formSchema = toTypedSchema(
    z.object({
      email: z.string({ message: 'home.user.validate.email' }).email({
        message: 'home.user.validate.emailValid',
      }),
      password: z
        .string({ message: 'home.user.validate.password' })
        .min(1, { message: 'home.user.validate.password' }),
    }),
  )

  const { handleSubmit, meta, resetForm } = useForm({
    validationSchema: formSchema,
    initialValues: {
      email: '',
      password: '',
    },
  })

  const resetFormState = () => {
    resetForm()
    showPassword.value = false
  }

  const handleCancel = () => {
    isOpen.value = false
  }

  const onSubmit = handleSubmit(async (values) => {
    const { code, data } = await userBind({
      userName: values.email,
      password: values.password,
    })

    if (code === 0) {
      toast.success(t('home.user.bindAccountDialog.success'))
      updateUserInfo(data)
      isOpen.value = false
    }
  })

  watch(isOpen, (value) => {
    if (!value) {
      resetFormState()
    }
  })
</script>

<template>
  <Dialog
    v-model:open="isOpen"
    :title="$t('home.user.bindAccountDialog.title')"
    content-class="w-[22.5rem] p-4"
  >
    <form id="bind-account-form" class="flex flex-col gap-3" autocomplete="off" @submit="onSubmit">
      <div class="space-y-3">
        <FormField v-slot="{ componentField, errorMessage }" name="email">
          <FormItem>
            <FormControl>
              <Input
                class="h-12 rounded-9xl text-text-active placeholder:text-text-explanatory-default"
                type="text"
                :placeholder="$t('home.user.emailPlaceholder2')"
                v-bind="componentField"
                autocomplete="username"
                :error-message="errorMessage ? $t(errorMessage) : undefined"
              >
                <template #prefix>
                  <Icon icon="Multimedia-Wireframe-Mail" class="size-4 text-black" />
                </template>
              </Input>
            </FormControl>
          </FormItem>
        </FormField>

        <FormField v-slot="{ componentField, errorMessage }" name="password">
          <FormItem>
            <FormControl>
              <Input
                class="h-12 rounded-9xl text-text-active placeholder:text-text-explanatory-default"
                :type="showPassword ? 'text' : 'password'"
                :placeholder="$t('home.user.passwordPlaceholder2')"
                v-bind="componentField"
                autocomplete="current-password"
                :error-message="errorMessage ? $t(errorMessage) : undefined"
              >
                <template #prefix>
                  <Icon icon="Function-Wireframe-Password" class="size-4 text-black" />
                </template>
                <template #suffix>
                  <Icon
                    :icon="showPassword ? 'Function-Wireframe-View' : 'Function-Wireframe-Hide'"
                    class="size-4 cursor-pointer pointer-events-auto text-black"
                    @click="showPassword = !showPassword"
                  />
                </template>
              </Input>
            </FormControl>
          </FormItem>
        </FormField>
      </div>

      <div class="mt-1 flex justify-end gap-3">
        <Button type="button" color="text-b-w" class="h-10" @click="handleCancel">
          {{ $t('common.cancel') }}
        </Button>
        <Button type="submit" color="black" class="h-10" :disabled="!meta.valid">
          {{ $t('common.confirm') }}
        </Button>
      </div>
    </form>
  </Dialog>
</template>
